import axios from 'axios';

import assetCategoriesApi from 'config/api/assetCategories/assetCategories';
import assetInformationChoicesApi from 'config/api/assetInformationChoices/assetInformationChoices';
import { assetsApi } from 'config/api/assets/assets';
import createUrlWithParams from 'helpers/createUrlWithParams';
import action_library_messages from 'messages/action_library_messages';
import booleanDictionary from 'services/_filters/booleanDictionary';

// Due Date Up To Filter
export const getDueDateUpToFilter = () => ({
  sectionName: action_library_messages.filters.due_date,
  filters: [
    { id: 'next_7_days', name: 'Next 7 days' },
    { id: 'next_30_days', name: 'Next 30 days' },
    { id: 'next_3_months', name: 'Next 3 months' },
  ],
  isSingleSelect: true,
  isPlaceholder: false,
});

// Is Overdue Filter
export const getIsOverdueFilter = () => ({
  sectionName: action_library_messages.filters.overdue,
  filters: booleanDictionary,
  isSingleSelect: true,
  isPlaceholder: false,
});

// Past Reminder Filter
export const getPastReminderFilter = () => ({
  sectionName: action_library_messages.filters.past_reminder,
  filters: booleanDictionary,
  isSingleSelect: true,
  isPlaceholder: false,
});

// Asset Importance Filter
export const getAssetImportanceFilter = async () => {
  const data = await assetInformationChoicesApi.getAssetInformationChoicesForFilters();
  return {
    sectionName: action_library_messages.filters.asset_importance,
    filters: data.assetsImportance,
    isSingleSelect: false,
    isPlaceholder: false,
  };
};

// Asset Type (category) Filter – filter by asset category ID
export const getAssetTypeFilter = async () => {
  const filters = await assetCategoriesApi.getAllAssetCategoriesForFilters();
  return {
    sectionName: action_library_messages.filters.asset_type,
    filters,
    isSingleSelect: false,
    isPlaceholder: false,
  };
};

// Sub-area Filter – options depend on selected area; without area returns empty list
export const getSubareaFilterForInstances = async () => ({
  sectionName: action_library_messages.filters.subarea,
  filters: [],
  isPlaceholder: false,
});

// Asset Filter – filter by Asset PK
export const getAssetFilter = async () => {
  const assets = await assetsApi.getAllAssets({ limit: 500 } as Parameters<typeof assetsApi.getAllAssets>[0]);
  return {
    sectionName: action_library_messages.filters.asset,
    filters: assets.map(a => ({ id: String(a.id), name: a.name })),
    isSingleSelect: false,
    isPlaceholder: false,
  };
};

const placeholderLabel = action_library_messages.filters.placeholder_badge;

// Responsible User Filter – current responsible (delegated_to when set, otherwise owner)
export const getResponsibleUserFilter = async () => {
  const { data } = await axios.get(createUrlWithParams('/api/v1/members/', { limit: 500 }));
  const results = data.results || data || [];
  return {
    sectionName: action_library_messages.filters.responsible_user,
    filters: results.map((u: { id: number; profile?: { first_name: string; last_name: string }; email?: string }) => ({
      id: String(u.id),
      name: u.profile ? `${u.profile.first_name} ${u.profile.last_name}`.trim() || u.email : u.email || String(u.id),
    })),
    isSingleSelect: false,
    isPlaceholder: false,
  };
};

// Kept for backward compatibility; use getResponsibleUserFilter for AC 5.1
export const getOwnerFilterForInstances = () => ({
  sectionName: action_library_messages.filters.responsible_user,
  filters: [],
  isPlaceholder: true,
  placeholderLabel,
});

// Instance Creation Date – preset ranges (created_from / created_to)
export const getInstanceCreationDateFilter = () => ({
  sectionName: action_library_messages.filters.instance_creation_date,
  filters: [
    { id: 'last_7_days', name: 'Last 7 days' },
    { id: 'last_30_days', name: 'Last 30 days' },
    { id: 'last_3_months', name: 'Last 3 months' },
  ],
  isSingleSelect: true,
  isPlaceholder: false,
});

// Source – template (from template) or adhoc (without template)
export const getSourceFilter = () => ({
  sectionName: action_library_messages.filters.source,
  filters: [
    { id: 'template', name: 'From template' },
    { id: 'adhoc', name: 'Ad hoc' },
  ],
  isSingleSelect: true,
  isPlaceholder: false,
});

// Validity – template validity configuration
export const getValidityFilter = () => ({
  sectionName: action_library_messages.filters.validity,
  filters: [
    { id: 'always_valid', name: 'Always valid' },
    { id: 'time_limited', name: 'Time limited' },
  ],
  isSingleSelect: true,
  isPlaceholder: false,
});

// Number of Steps – exact count
export const getNumberOfStepsFilter = () => ({
  sectionName: action_library_messages.filters.number_of_steps,
  filters: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => ({ id: String(n), name: String(n) })),
  isSingleSelect: true,
  isPlaceholder: false,
});

// Tags – template tags (comma-separated); empty until BE exposes tag list for filter options
export const getTagsFilter = () => ({
  sectionName: action_library_messages.filters.tags,
  filters: [],
  isSingleSelect: false,
  isPlaceholder: false,
});
